import { ApiRequestError } from "@nexxt/api-client";

// Push notifications in this browser (ADR-283): what it can do, and turning them on and off. One subscription per
// device, made only when the person asks: the permission prompt of the browser comes from their click, the service
// worker is registered then and not at the start. The gateway of the app keeps the subscription for its person.

/** The three routes of the app's gateway (`/notifications/push…`). */
export interface PushClient {
  settings(): Promise<{ publicKey: string | null }>;
  subscribe(input: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<unknown>;
  unsubscribe(input: { endpoint: string }): Promise<unknown>;
}

/**
 * `unsupported`: this browser has no Web Push; `install`: an iPhone or iPad, where pushes reach only the app added to
 * the Home Screen; `denied`: the person blocked the notifications of the site, which only the browser's settings
 * undo; `off` and `on`: this device.
 */
export type PushState = "unsupported" | "install" | "denied" | "off" | "on";

const SCRIPT = "/sw.js";

const isAppleMobile = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const installed = () =>
  matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;

function support(): "supported" | "unsupported" | "install" {
  if ("serviceWorker" in navigator && "PushManager" in window && "Notification" in window) return "supported";
  return isAppleMobile() && !installed() ? "install" : "unsupported";
}

const registration = () => navigator.serviceWorker.getRegistration("/");

async function subscription(): Promise<PushSubscription | null> {
  return (await registration())?.pushManager.getSubscription() ?? null;
}

/**
 * What this device can do now, and whether it is on. A subscription made for another key of the environment (one
 * rotated) receives nothing, so it is off. One that is on is renewed on the gateway (`subscribe` is idempotent): the
 * device comes back if the purge or the end of a session took it away, and its keys are the browser's of now; if the
 * browser was on for another person (409), it is taken away here and the device is off.
 */
export async function readPushState(publicKey: string, client: Pick<PushClient, "subscribe">): Promise<PushState> {
  const supported = support();
  if (supported !== "supported") return supported;
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission !== "granted") return "off";
  const current = await subscription();
  if (!current || !sameKey(current.options.applicationServerKey, bytes(publicKey))) return "off";
  try {
    await save(client, current);
  } catch (error) {
    if (!taken(error)) return "on";
    await current.unsubscribe();
    return "off";
  }
  return "on";
}

function bytes(base64url: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64url.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

const sameKey = (a: ArrayBuffer | null, b: Uint8Array) =>
  a !== null && a.byteLength === b.length && new Uint8Array(a).every((byte, i) => byte === b[i]);

const taken = (error: unknown) => error instanceof ApiRequestError && error.code === "push_endpoint_taken";

/** The gateway keeps the subscription for its person, or renews it. */
function save(client: Pick<PushClient, "subscribe">, s: PushSubscription) {
  const json = s.toJSON();
  return client.subscribe({
    endpoint: s.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
  });
}

/**
 * Turns pushes on here, from a click: the permission of the browser, the service worker, a subscription for the key
 * of the environment (a new one when the key changed), and the gateway keeps it. A subscription another person
 * turned on in this browser is replaced once by a new one: an endpoint never moves between people.
 */
export async function turnPushOn(client: PushClient): Promise<"on" | "denied" | "unavailable"> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "unavailable";
  const { publicKey } = await client.settings();
  if (!publicKey) return "unavailable";
  const key = bytes(publicKey);
  await navigator.serviceWorker.register(SCRIPT, { scope: "/" });
  const ready = await navigator.serviceWorker.ready;
  const fresh = () => ready.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  let current = await ready.pushManager.getSubscription();
  if (current && !sameKey(current.options.applicationServerKey, key)) {
    await current.unsubscribe();
    current = null;
  }
  current ??= await fresh();
  try {
    await save(client, current);
  } catch (error) {
    if (!taken(error)) throw error;
    await current.unsubscribe();
    await save(client, await fresh());
  }
  return "on";
}

/** Turns pushes off here: the gateway forgets the device, then the browser its subscription. */
export async function turnPushOff(client: PushClient): Promise<void> {
  const current = await subscription();
  if (!current) return;
  await client.unsubscribe({ endpoint: current.endpoint });
  await current.unsubscribe();
}

/**
 * Before signing out (ADR-283): the device stops receiving the person's pushes, so the next person in this browser
 * gets none of theirs. Never throws and never waits more than two seconds: signing out goes on whatever happens.
 */
export async function forgetPushDevice(client: Pick<PushClient, "unsubscribe">): Promise<void> {
  if (support() !== "supported") return;
  const forget = async () => {
    const current = await subscription();
    if (!current) return;
    await client.unsubscribe({ endpoint: current.endpoint }).catch(() => undefined);
    await current.unsubscribe();
  };
  await Promise.race([forget().catch(() => undefined), new Promise((resolve) => setTimeout(resolve, 2000))]);
}
